import { useEffect, useMemo } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { SearchableSelect } from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useAccounts } from "~/hooks/contabilidad/useAccounts";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";

import type {
  AccountingCategory,
  AccountingCategoryFormData,
} from "~/types/contabilidad";

const formSchema = z.object({
  company_id: z.string().min(1, "Seleccione una empresa"),
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  costing_method: z.enum(["average", "standard", "fifo"]),
  inventory_valuation: z.enum(["manual", "perpetual"]),
  income_account_id: z.string().nullable().optional(),
  expense_account_id: z.string().nullable().optional(),
  inventory_account_id: z.string().nullable().optional(),
  inventory_variation_account_id: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const COSTING_METHODS: { value: FormValues["costing_method"]; label: string }[] =
  [
    { value: "average", label: "Promedio (AVCO)" },
    { value: "standard", label: "Estándar" },
    { value: "fifo", label: "FIFO" },
  ];

const INVENTORY_VALUATIONS: {
  value: FormValues["inventory_valuation"];
  label: string;
}[] = [
  { value: "perpetual", label: "Permanente (al facturar)" },
  { value: "manual", label: "Periódico (manual)" },
];

interface CategoriaContableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: AccountingCategory;
  onSubmit: (data: AccountingCategoryFormData) => void;
  isLoading?: boolean;
  defaultCompanyId?: string;
}

// The 4 account assignments, each a searchable picker scoped to the category's company.
const ACCOUNT_FIELDS = [
  { name: "income_account_id", label: "Cuenta de Ingresos" },
  { name: "expense_account_id", label: "Cuenta de Gastos" },
  { name: "inventory_account_id", label: "Cuenta de Inventario" },
  {
    name: "inventory_variation_account_id",
    label: "Variación de Inventario",
  },
] as const;

export function CategoriaContableDialog({
  open,
  onOpenChange,
  category,
  onSubmit,
  isLoading = false,
  defaultCompanyId,
}: CategoriaContableDialogProps) {
  const isEditing = !!category;
  const { data: companies = [] } = useCompanies();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_id: category?.company_id ?? defaultCompanyId ?? "",
      name: category?.name ?? "",
      costing_method: category?.costing_method ?? "average",
      inventory_valuation: category?.inventory_valuation ?? "perpetual",
      income_account_id: category?.income_account_id ?? null,
      expense_account_id: category?.expense_account_id ?? null,
      inventory_account_id: category?.inventory_account_id ?? null,
      inventory_variation_account_id:
        category?.inventory_variation_account_id ?? null,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        company_id: category?.company_id ?? defaultCompanyId ?? "",
        name: category?.name ?? "",
        costing_method: category?.costing_method ?? "average",
        inventory_valuation: category?.inventory_valuation ?? "perpetual",
        income_account_id: category?.income_account_id ?? null,
        expense_account_id: category?.expense_account_id ?? null,
        inventory_account_id: category?.inventory_account_id ?? null,
        inventory_variation_account_id:
          category?.inventory_variation_account_id ?? null,
      });
    }
  }, [open, category, defaultCompanyId, form]);

  const companyId = form.watch("company_id");
  const { data: accounts = [] } = useAccounts({
    company_id: companyId || undefined,
    is_active: true,
  });

  const accountOptions = useMemo(
    () =>
      accounts
        .slice()
        .sort((a, b) => a.code.localeCompare(b.code))
        .map((a) => ({
          value: a.id,
          label: `${a.code} · ${a.name}`,
          searchTerms: `${a.code} ${a.name}`,
        })),
    [accounts]
  );

  const handleSubmit = (data: FormValues) => onSubmit(data);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Categoría" : "Nueva Categoría"}
          </DialogTitle>
          <DialogDescription>
            Configure la valoración y la asignación de cuentas contables de la
            categoría.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="company_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Empresa *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={isEditing}
                  >
                    <FormControl>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Seleccione una empresa" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categoría *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ej: Productos Terminados"
                      className="h-9"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="costing_method"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Método de costo</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {COSTING_METHODS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="inventory_valuation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valoración inventario</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {INVENTORY_VALUATIONS.map((v) => (
                          <SelectItem key={v.value} value={v.value}>
                            {v.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-700">
                Asignación de cuentas
              </p>
              <p className="text-xs text-muted-foreground">
                {companyId
                  ? "Cuentas del plan contable de la empresa seleccionada."
                  : "Seleccione una empresa para elegir las cuentas."}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {ACCOUNT_FIELDS.map((f) => (
                <FormField
                  key={f.name}
                  control={form.control}
                  name={f.name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{f.label}</FormLabel>
                      <FormControl>
                        <SearchableSelect
                          value={field.value ?? ""}
                          onValueChange={field.onChange}
                          options={accountOptions}
                          placeholder="Seleccionar cuenta…"
                          emptyMessage={
                            companyId ? "Sin cuentas" : "Elija una empresa"
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? "Actualizar" : "Crear Categoría"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
